import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {connect} from 'react-redux';
import {Alert, Button, Divider, Form, Input, Radio, Select} from 'antd';
import {StyledAddUserModal} from './StyledAddUserModal';
import Accordion from '../../../Accordion';
import SpinSmall from '../../../SpinSmall';
import CompanyTagSelect from '../../TransactionsPage/CompanyTagSelect/CompanyTagSelect';
import {formHelpers, UserRoleOptions} from '../../../../utils/helpers';
import {tagFamilyConstants, userRolesConstants} from '../../../../constants';
import {useFormHelpers} from '../../../../hooks';

const {Item} = Form;

const defaultFormValues = {
  email: '',
  external_id: '',
  job_title: '',
  report_to: undefined,
  role: `${userRolesConstants.ADMIN}`,
  team_id: undefined,
};

const defaultError = {
  message: null,
  open: false
};

const AddUserModal = ({
  dispatch,
  employees,
  employeeEmail,

  handleOk,
  handleCancel,
  loading = false,
  mode = 'add',
  open,
  user,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [rolesOptions, ] = useState(UserRoleOptions().sort((a, b) => a.label.localeCompare(b.label)));
  const [form] = Form.useForm();
  const [error, setError] = useState(defaultError);
  const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);

  const isEditMode = mode === 'edit';

  const {getEmployeeOptions, placeholderEnter, placeholderSelect} = useFormHelpers();

  const employeeOptions = getEmployeeOptions({excludeEmployeeEmail: true})

  useEffect(() => {
    if (open && mode === 'edit' && user) {
      const teamId = user?.team_id;
      const initialValues = {
        email: user?.email || '',
        external_id: user?.external_id || '',
        job_title: user?.job_title || '',
        report_to: user?.report_to || undefined,
        role: user?.role,
        team: teamId ? [teamId] : [],
        team_id: teamId
      }
      setInitialFormValues(initialValues);
      form.setFieldsValue(initialValues);
    }
  }, [form, mode, open, user]);

  const handleAfterOpenChange = (open) => {
    if (!open) {
      form.setFieldsValue(defaultFormValues);
      error && setError(defaultError);
    }
  }

  const handleSubmit = (fields) => {
    // remove email value from fields if isEditMode
    if (isEditMode) delete fields.email;

    handleOk?.(
      fields,
      (err) => {
        if (err.message) {
          setError({
            ...error,
            message: err.message,
            open: true
          });
        } else {
          const fields = formHelpers.getFormServerErrorFields(err);
          form.setFields(fields);
        }
      }
    )
  }

  const onChangeTags = (tags) => {
    form.setFieldValue('team_id', tags?.[0] || undefined);
    setInitialFormValues({...initialFormValues, team: tags});
  }

  const rules = [{required: true, message: t('validation.fieldIsRequired')}];

  const actionTitle = t(mode === 'add' ? 'invite' : 'edit');

  const footer = [
    <Button
      key='next'
      disabled={loading}
      onClick={() => form.submit()}
      size='large'
      type='primary'
    >
      {actionTitle}
    </Button>
  ];

  return (
    <StyledAddUserModal
      afterOpenChange={handleAfterOpenChange}
      footer={footer}
      forceRender
      onClose={handleCancel}
      open={open}
      title={`${actionTitle} ${t('user')}`}
      width={480}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Item
            label={t('email')}
            name='email'
            rules={rules}
            required
          >
            <Input
              disabled={isEditMode}
              placeholder={placeholderEnter('email')}
              size='large'
              type='email'
            />
          </Item>
          <Item
            label={t('role')}
            name='role'
            rules={rules}
            required
          >
            <Radio.Group>
              {rolesOptions.map(({label, value}) => <Radio key={value} value={value}>{label}</Radio>)}
            </Radio.Group>
          </Item>
          <Divider />
          <Accordion
            defaultActiveKey={['1']}
            items={[
              {
                key: '1',
                label: `${t('team')} ${t('structure')}`,
                forceRender: true,
                children: (
                  <>
                    <Item label={t('jobTitle')} name='job_title'>
                      <Input placeholder={placeholderEnter('jobTitle')} size='large' />
                    </Item>
                    <Item
                      label={t('team')}
                      name='team_id'
                    >
                      <CompanyTagSelect
                        dropdownProps={{ overlayStyle: null }}
                        inputProps={{
                          placeholder: placeholderSelect('team'),
                          size: 'large'
                        }}
                        optionValueName='id'
                        tagFamily={tagFamilyConstants.TEAM}
                        variant='single-select'
                        onChange={onChangeTags}
                        selected={initialFormValues.team}
                      />
                    </Item>
                    <Item
                      label={t('reportsTo')}
                      name='report_to'
                    >
                      <Select
                        optionFilterProp='optionLabel'
                        options={employeeOptions}
                        placeholder={placeholderSelect('reportsTo')}
                        size='large'
                        showSearch
                      />
                    </Item>
                    <Item label={t('externalId')} name='external_id'>
                      <Input placeholder={placeholderEnter('externalId')} size='large' />
                    </Item>
                  </>
                )
              }
            ]}
          />

        </Form>
        <Collapse isOpened={error.open}>
          <Alert message={error.message} type='error' showIcon />
        </Collapse>
      </SpinSmall>
    </StyledAddUserModal>
  );
}

AddUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  mode: PropTypes.oneOf(['add', 'edit']),
  user: PropTypes.object,
}

const mapStateToProps = ({ company: { employees }, user: { employee } }) => ({
  employees,
  employeeEmail: employee?.email,
});

export default connect(mapStateToProps, null)(AddUserModal);
